// ============================================================================
// Notifications API — notifications that did NOT send (the same set the backend
// failure-report email covers). Self-contained axios calls on the app's default
// instance (baseURL + JWT set globally in src/index.tsx), so relative paths
// inherit auth. Backend: order/notification_service/api/ (DRF, PageNumberPagination).
// ============================================================================
import axios from "axios";
import { errorActions } from "../../helpers/auth_helper";

// One "did not send" notification row.
export interface NotificationFailureRow {
  id: number;
  notification_type: string | null;
  channel: string | null;
  receiver_id: string | null;
  receiver_type: string | null;
  receiver_name: string | null; // pulled from payload
  receiver_contact: string | null; // pulled from payload (contact/phone)
  status: string; // PENDING | RETRIED | FAILED | UNCERTAIN
  remark: string | null; // human-readable failure reason
  created_at: string | null;
  updated_at: string | null;
}

// DRF PageNumberPagination envelope.
export interface NotificationFailureListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: NotificationFailureRow[];
}

export interface ListNotificationFailureParams {
  status?: string;
  notification_type?: string;
  channel?: string;
  date_from?: string; // YYYY-MM-DD, created_at lower bound (default: today)
  date_to?: string; // YYYY-MM-DD, created_at upper bound (default: today)
  search?: string; // matches receiver / reason / type / payload (lead id, PO no…)
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface NotificationFailureSummary {
  total: number;
  by_status: Record<string, number>;
  by_type: { notification_type: string | null; n: number }[];
}

// Drop empty/false params so the backend defaults apply (e.g. absent include_recent = false).
function clean(params: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || v === false) return;
    out[k] = v === true ? "true" : v;
  });
  return out;
}

export async function listNotificationFailures(
  params: ListNotificationFailureParams = {}
): Promise<NotificationFailureListResponse | undefined> {
  try {
    return await axios
      .get("/notifications/failures/", { params: clean(params) })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading notifications");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

export async function getNotificationFailureSummary(
  params: Pick<ListNotificationFailureParams, "date_from" | "date_to"> = {}
): Promise<NotificationFailureSummary | undefined> {
  try {
    return await axios
      .get("/notifications/failures/summary/", { params: clean(params) })
      .then((res) => res?.data)
      .catch(() => undefined);
  } catch {
    return undefined;
  }
}
